/**
 * 授权操作管理页面 - 左右分栏布局
 * 左侧：操作树形结构选择器
 * 右侧：操作详情表单（属性、角色、管理员）
 */
import React, { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { Row, Col, Button, Card, Tag, Modal, message } from "antd";
import { GenericModalForm, ContainerPanel, CompsTree, CompsDescription } from "@ms/shared-components";
import ContainerModal from "@ms/shared-components/containers/ContainerModal";
import CompsForm from "@ms/shared-components/comps/CompsForm";
// Role/Admin 列表改为标签展示，移除组件引用
import { DeleteOutlined } from "@ant-design/icons";
import { usePermissionEnums } from "./hooks/usePermissionEnums";
import { batchDeleteOperations } from "./services/operationService";
import type { PermissionTreeNodeData } from "@ms/shared-components/comps/CompsTree";

import { usePermissionManagement } from "./hooks/usePermissionManagement";
import { usePermissionFormConfig } from "./config/modalFormConfig";
import { usePermissionTree } from "./hooks/usePermissionTree";
import { usePermissionHandlers } from "./hooks/usePermissionHandlers";
import { convertPermissionTreeToTreeNodeData } from "../assignment/utils/permissionTreeUtils";
import { createModalConfig, editModalConfig } from "./config/modalFormConfig";
import { treeCardConfig } from "./config/panelTreeConfig";
import { getDetailPanelButtons } from "./config/panelDetailConfig";
import { setChineseLabels, getTreePanelButtons } from "./config/panelTreeConfig";
import { getPermissionDescriptionData } from "./config/descriptionConfig";
import { ROLE_CARD_TITLE } from "./config/cardRoleConfig";
import { ADMIN_CARD_TITLE } from "./config/cardAdminConfig";
import {
  containerStyles,
  rowStyles,
  leftColStyles,
  rightColStyles,
  treeCardStyles,
  extraActionsStyles,
  selectedServiceInfoStyles,
  refreshButtonStyles,
  addButtonStyles,
} from "./config/pageConfig";

const OperationsPage: React.FC = () => {
  const {
    permissionTree,
    selectedOperation,
    rolesWithPermission,
    adminsWithPermission,
    selectedService,
    selectedPage,
    selectedNodeCode,
    loading,
    permissionLoading,
    rolesLoading,
    adminsLoading,
    loadPermissionTree,
    handlePermissionSelection,
    handleTreeNodeSelect,
    reloadPermissionDetails,
    clearSelection,
  } = usePermissionTree();

  // Custom Hooks
  const { serviceOptions, pageOptions, actionOptions, statusOptions, typeOptions } = usePermissionEnums();

  const {
    createModalVisible,
    editModalVisible,
    currentRecord,
    handleCreate,
    handleEdit,
    handleDelete: handleDeletePermission,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
  } = usePermissionManagement();

  const { getCreateFormConfig, getEditFormConfig } = usePermissionFormConfig();

  // 权限操作处理
  const { handleCreateWrapper, handleEditWrapper, handleDeletePermissionClick } = usePermissionHandlers({
    handleCreate,
    handleEdit,
    handleDelete: handleDeletePermission,
    openEditModal,
    loadPermissionTree,
    clearSelection,
    reloadPermissionDetails,
    selectedOperation,
  });

  // 删除确认对话框状态
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 打开删除确认对话框
  const handleOpenDeleteModal = useCallback(() => {
    if (selectedOperation) {
      setDeleteModalVisible(true);
    }
  }, [selectedOperation]);

  // 关闭删除确认对话框
  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalVisible(false);
  }, []);

  // 确认删除权限
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedOperation) {
      return;
    }

    setDeleting(true);
    try {
      const result = await handleDeletePermissionClick(selectedOperation);
      if (result) {
        setDeleteModalVisible(false);
      }
    } catch (error: any) {
      console.error("删除权限失败:", error);
      message.error(error?.message || "删除权限失败");
    } finally {
      setDeleting(false);
    }
  }, [selectedOperation, handleDeletePermissionClick]);

  // 初始化加载权限树
  useEffect(() => {
    loadPermissionTree();
  }, [loadPermissionTree]);

  // 仅注入“服务”的中文映射，并刷新权限树（页面与操作保持英文）
  useEffect(() => {
    try {
      const serviceMap =
        (serviceOptions || [])
          .filter((opt: any) => opt && opt.value) // 过滤 "全部" 等空值
          .reduce((acc: Record<string, string>, opt: any) => {
            acc[opt.value] = opt.label || opt.value;
            return acc;
          }, {}) || {};

      setChineseLabels({
        services: serviceMap,
        pages: {},
        actions: {},
      });

      // 更新映射后刷新一次树，确保标题显示中文
      loadPermissionTree();
    } catch (e) {
      // 忽略映射失败，不影响基本功能
    }
  }, [serviceOptions, setChineseLabels, loadPermissionTree]);

  // 转换权限树数据为 TreeNodeData
  const treeData = useMemo(() => {
    return convertPermissionTreeToTreeNodeData(permissionTree, [], {});
  }, [permissionTree]);

  // 处理节点选择
  const handleSelect = (selectedKeys: React.Key[], info: any) => {
    if (handleTreeNodeSelect) {
      handleTreeNodeSelect(selectedKeys as string[]);
    }
    // 如果选中了节点，触发选择变化
    if (selectedKeys.length > 0 && handlePermissionSelection) {
      const selectedKey = selectedKeys[0] as string;
      // 查找对应的权限项
      const findPermission = (permissions: any[], targetId: string): any | null => {
        for (const permission of permissions) {
          if (permission.id === targetId) {
            return permission;
          }
          if (permission.children) {
            const found = findPermission(permission.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };
      const selectedPermission = findPermission(permissionTree, selectedKey);
      if (selectedPermission) {
        handlePermissionSelection([selectedPermission]);
      }
    }
  };

  // 从权限树中收集某个页面的所有权限ID
  const collectPagePermissionIds = useCallback(
    (serviceName: string, page: string): string[] => {
      const ids: string[] = [];

      const traverse = (nodes: any[]) => {
        for (const node of nodes) {
          // 如果是页面节点，检查是否匹配
          if (node.id?.startsWith("page_") && node.permissionData) {
            const nodeService = node.permissionData.serviceName;
            const nodePage = node.permissionData.name;
            if (nodeService === serviceName && nodePage === page) {
              // 收集该页面下所有子节点（权限）的ID
              if (node.children) {
                node.children.forEach((child: any) => {
                  if (child.type === "FUNCTION" && child.id) {
                    ids.push(child.id);
                  }
                });
              }
            }
          }
          // 递归遍历子节点
          if (node.children) {
            traverse(node.children);
          }
        }
      };

      traverse(permissionTree);
      return ids;
    },
    [permissionTree]
  );

  // 删除页面所有权限
  const handleDeletePagePermissions = useCallback(
    async (node: PermissionTreeNodeData) => {
      // 检查是否为页面节点
      if (!node.key?.toString().startsWith("page_")) {
        return;
      }

      // 从节点中提取 service 和 page 信息
      const permissionData = node.permission || (node as any).permissionData;
      if (!permissionData) {
        message.error("无法获取页面信息");
        return;
      }

      const serviceName = permissionData.serviceName;
      const page = permissionData.name || permissionData.page;

      if (!serviceName || !page) {
        message.error("无法获取服务或页面信息");
        return;
      }

      // 收集该页面的所有权限ID
      const permissionIds = collectPagePermissionIds(serviceName, page);

      if (permissionIds.length === 0) {
        message.warning("该页面下没有权限可删除");
        return;
      }

      // 确认删除
      Modal.confirm({
        title: "确认删除",
        content: `确定要删除页面 "${serviceName}:${page}" 下的所有 ${permissionIds.length} 个权限吗？此操作不可恢复。`,
        okText: "确定",
        cancelText: "取消",
        okType: "danger",
        onOk: async () => {
          try {
            await batchDeleteOperations(permissionIds);
            message.success(`成功删除 ${permissionIds.length} 个权限`);
            // 刷新权限树
            await loadPermissionTree();
            // 清空选择
            clearSelection();
          } catch (error: any) {
            console.error("删除页面权限失败:", error);
            message.error(error?.message || "删除页面权限失败");
          }
        },
      });
    },
    [collectPagePermissionIds, loadPermissionTree, clearSelection]
  );

  // 自定义菜单按钮：为页面节点添加删除按钮
  const customMenuButton = useCallback(
    (node: PermissionTreeNodeData) => {
      // 只对页面节点（type === "MODULE" 且 id 以 "page_" 开头）显示删除按钮
      const isPageNode = node.type === "MODULE" && node.key?.toString().startsWith("page_");

      if (!isPageNode) {
        return null;
      }

      return (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          size="small"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleDeletePagePermissions(node);
          }}
          style={{
            padding: "0 4px",
            color: "#bfbfbf",
            fontSize: "12px",
          }}
          title="删除该页面的所有权限"
        />
      );
    },
    [handleDeletePagePermissions]
  );

  return (
    <div className="permission-management-container" style={containerStyles}>
      <Row gutter={16} style={rowStyles}>
        {/* 左侧：权限树 */}
        <Col style={leftColStyles}>
          <ContainerPanel
            title={treeCardConfig.title}
            buttons={getTreePanelButtons({
              onRefresh: loadPermissionTree,
              onAdd: openCreateModal,
            })}
            height="100%"
            cardProps={{ style: treeCardStyles }}
          >
            <CompsTree
              treeData={treeData as any}
              loading={loading}
              checkable={treeCardConfig.showCheckbox}
              showSearch={treeCardConfig.showSearch}
              searchPlaceholder="搜索微服务、页面、操作..."
              defaultExpandAll={treeCardConfig.defaultExpandAll}
              onSelect={handleSelect}
              selectedKeys={selectedOperation?.id ? [selectedOperation.id] : []}
              height="100%"
              customMenuButton={customMenuButton}
            />
          </ContainerPanel>
        </Col>

        {/* 右侧：权限详情（容器 + 描述 + 两个卡片） */}
        <Col style={rightColStyles}>
          <ContainerPanel
            title="权限详情"
            height="100%"
            buttons={getDetailPanelButtons({
              selectedOperation,
              onDelete: handleOpenDeleteModal,
            })}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
              <CompsDescription
                columns={1}
                data={getPermissionDescriptionData(selectedNodeCode, selectedOperation, serviceOptions)}
                bordered
                styles={{
                  label: { width: 120 },
                }}
              />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, flex: 1, minHeight: 0 }}>
                <Card
                  title={`${ROLE_CARD_TITLE} (${(rolesWithPermission || []).length})`}
                  size="small"
                  style={{ height: "100%" }}
                  loading={rolesLoading}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {(rolesWithPermission || []).map((r: any) => (
                      <Tag key={r.id}>{r.name || r.roleName || "-"}</Tag>
                    ))}
                  </div>
                </Card>
                <Card
                  title={`${ADMIN_CARD_TITLE} (${(adminsWithPermission || []).length})`}
                  size="small"
                  style={{ height: "100%" }}
                  loading={adminsLoading}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {(adminsWithPermission || []).map((a: any) => (
                      <Tag key={a.id}>{a.name || a.adminName || a.account || "-"}</Tag>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          </ContainerPanel>
        </Col>
      </Row>

      {/* 创建权限模态框 */}
      {(() => {
        const createFormRef = useRef<any>(null);
        return (
          <ContainerModal
            title={createModalConfig.title}
            visible={createModalVisible}
            width={createModalConfig.width}
            height={createModalConfig.height}
            onCancel={closeCreateModal}
            footerRightButtons={[
              {
                key: "cancel",
                text: createModalConfig.cancelText,
                type: "default",
                onClick: closeCreateModal,
              },
              {
                key: "ok",
                text: createModalConfig.okText,
                type: "primary",
                onClick: () => createFormRef.current?.submit(),
              },
            ]}
            modalProps={createModalConfig.modalProps}
          >
            <CompsForm
              formItems={getCreateFormConfig()}
              formRef={(form) => (createFormRef.current = form)}
              initialValues={{
                serviceName: selectedService || undefined,
                page: selectedPage || undefined,
              }}
              layout={createModalConfig.layout}
              labelCol={createModalConfig.labelCol}
              wrapperCol={createModalConfig.wrapperCol}
              formProps={{
                onFinish: handleCreateWrapper,
              }}
            />
          </ContainerModal>
        );
      })()}

      {/* 编辑权限模态框 */}
      {(() => {
        const editFormRef = useRef<any>(null);
        return (
          <ContainerModal
            title={editModalConfig.title}
            visible={editModalVisible}
            width={editModalConfig.width}
            height={editModalConfig.height}
            onCancel={closeEditModal}
            footerRightButtons={[
              {
                key: "cancel",
                text: editModalConfig.cancelText,
                type: "default",
                onClick: closeEditModal,
              },
              {
                key: "ok",
                text: editModalConfig.okText,
                type: "primary",
                onClick: () => editFormRef.current?.submit(),
              },
            ]}
            modalProps={editModalConfig.modalProps}
          >
            <CompsForm
              formItems={getEditFormConfig()}
              formRef={(form) => (editFormRef.current = form)}
              initialValues={
                currentRecord
                  ? {
                      name: currentRecord.name,
                      description: currentRecord.description,
                      serviceName: currentRecord.serviceName,
                      page: currentRecord.page,
                      action: currentRecord.action,
                      isActive: currentRecord.isActive ? "active" : "inactive",
                    }
                  : undefined
              }
              layout={editModalConfig.layout}
              labelCol={editModalConfig.labelCol}
              wrapperCol={editModalConfig.wrapperCol}
              formProps={{
                onFinish: handleEditWrapper,
              }}
            />
          </ContainerModal>
        );
      })()}

      {/* 删除权限确认对话框 */}
      {selectedOperation && (
        <ContainerModal
          title="删除权限"
          visible={deleteModalVisible}
          width={600}
          onCancel={handleCloseDeleteModal}
          footerRightButtons={[
            {
              key: "cancel",
              text: "取消",
              type: "default",
              onClick: handleCloseDeleteModal,
            },
            {
              key: "delete",
              text: "删除",
              type: "danger",
              icon: React.createElement(DeleteOutlined),
              loading: deleting,
              onClick: handleConfirmDelete,
            },
          ]}
          modalProps={{
            destroyOnClose: true,
          }}
        >
          <div style={{ padding: "16px 0" }}>
            <p style={{ marginBottom: 16, fontSize: 14, color: "#666" }}>
              确定要删除以下权限吗？此操作不可恢复。
            </p>
            <CompsDescription
              columns={1}
              data={[
                {
                  key: "code",
                  label: "权限代码",
                  value: selectedOperation.code || selectedNodeCode || "-",
                },
                {
                  key: "name",
                  label: "权限名称",
                  value: getPermissionDescriptionData(selectedNodeCode, selectedOperation, serviceOptions).find(
                    (item) => item.key === "name"
                  )?.value || "-",
                },
                {
                  key: "serviceName",
                  label: "微服务",
                  value: selectedOperation.serviceName || "-",
                },
                {
                  key: "page",
                  label: "页面",
                  value: selectedOperation.page || "-",
                },
                {
                  key: "action",
                  label: "操作",
                  value: selectedOperation.action || "-",
                },
                {
                  key: "description",
                  label: "描述",
                  value: selectedOperation.description || "-",
                },
              ]}
              bordered
              styles={{
                label: { width: 120 },
              }}
            />
            {(rolesWithPermission && rolesWithPermission.length > 0) ||
            (adminsWithPermission && adminsWithPermission.length > 0) ? (
              <div style={{ marginTop: 24, padding: 16, backgroundColor: "#fff7e6", border: "1px solid #ffd591", borderRadius: 4 }}>
                <p style={{ marginBottom: 12, fontSize: 14, fontWeight: 500, color: "#d46b08" }}>
                  警告：此权限正在被使用
                </p>
                <p style={{ marginBottom: 12, fontSize: 13, color: "#666" }}>
                  删除此权限将同时删除以下关联的权限分配：
                </p>
                {rolesWithPermission && rolesWithPermission.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ marginBottom: 8, fontSize: 13, color: "#666" }}>
                      <strong>关联角色 ({rolesWithPermission.length} 个)：</strong>
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {rolesWithPermission.map((role: any) => (
                        <Tag key={role.id} color="orange">
                          {role.name || role.code}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
                {adminsWithPermission && adminsWithPermission.length > 0 && (
                  <div>
                    <p style={{ marginBottom: 8, fontSize: 13, color: "#666" }}>
                      <strong>关联管理员 ({adminsWithPermission.length} 个)：</strong>
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {adminsWithPermission.map((admin: any) => (
                        <Tag key={admin.id} color="orange">
                          {admin.account || admin.admin_name}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4 }}>
                <p style={{ margin: 0, fontSize: 13, color: "#52c41a" }}>
                  此权限未被任何角色或管理员使用，可以安全删除。
                </p>
              </div>
            )}
          </div>
        </ContainerModal>
      )}
    </div>
  );
};

export default OperationsPage;
